import React, { useState } from 'react';
import { Outlet, useNavigate, NavLink as RouterLink } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Category as CategoryIcon,
    Inventory as InventoryIcon,
    ShoppingBag as ShoppingBagIcon,
    ExitToApp as ExitToAppIcon,
    Chat as ChatIconNav
} from '@mui/icons-material';
import { logout } from '../../../services/authService';
import { COLORS } from '../../../assets/colors';
import { ROUTES_PATH } from '../../../utils/Consts';

const drawerWidth = 260;

const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: ROUTES_PATH.ADMIN_DASHBOARD },
    { text: 'Categorías', icon: <CategoryIcon />, path: ROUTES_PATH.ADMIN_CATEGORIES },
    { text: 'Subcategorías', icon: <InventoryIcon />, path: ROUTES_PATH.ADMIN_SUBCATEGORIES },
    { text: 'Productos', icon: <ShoppingBagIcon />, path: ROUTES_PATH.ADMIN_PRODUCTS },
    { text: 'Chat', icon: <ChatIconNav />, path: ROUTES_PATH.ADMIN_CHAT },
];

function AdminLayout() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    const drawer = (
        <Box>
            <Toolbar sx={{
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, rgba(30, 58, 95, 0.95) 100%)`,
                minHeight: '80px !important'
            }}>
                <Typography variant="h6" sx={{
                    fontFamily: 'Quicksand',
                    fontWeight: 'bold',
                    color: COLORS.secondary,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    El Rosal Admin
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to={item.path}
                            onClick={isMobile ? handleDrawerToggle : undefined}
                            sx={{
                                '&.active': {
                                    backgroundColor: `${COLORS.secondary}20`,
                                    borderRight: `4px solid ${COLORS.secondary}`,
                                    '& .MuiListItemIcon-root': {
                                        color: COLORS.secondary
                                    },
                                    '& .MuiListItemText-primary': {
                                        color: COLORS.secondary,
                                        fontWeight: 'bold'
                                    }
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: COLORS.primary }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontFamily: 'Quicksand',
                                    fontWeight: 600
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider />
            <List>
                <ListItem disablePadding>
                    <ListItemButton onClick={handleLogout}>
                        <ListItemIcon sx={{ color: '#dc3545' }}>
                            <ExitToAppIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Cerrar Sesión"
                            primaryTypographyProps={{
                                fontFamily: 'Quicksand',
                                fontWeight: 600,
                                color: '#dc3545'
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    background: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' }, color: COLORS.primary }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{
                        fontFamily: 'Quicksand',
                        fontWeight: 'bold',
                        color: COLORS.primary
                    }}>
                        Panel de Administración
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant={isMobile ? "temporary" : "permanent"}
                    open={isMobile ? mobileOpen : true}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: `1px solid ${COLORS.secondary}30`
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    mt: '64px'
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
}

export default AdminLayout;
